import { Injectable } from '@angular/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ReusableStreamProperty, equalsCompare } from '@ipreo/muni-shared';
import { ExportBaseService } from '../../gameday/services/export-base.service';
import { TabSettings } from '../../gameday/models/report';
import { toMillion } from '../../gameday/components/excel-export/excel.formatter';
import {
  performFinalTransformations,
  transformTotals,
  sortOrderMonitorItemByMaturity
} from '../mappers/monitor-base-page.calculations';
import { MonitorOverviewViewItem } from '../models/monitor';
import { MonitorService } from './monitor.service';
import { MonitorOverviewPageService } from './monitor-overview-page.service';
import { MonitorRetailPageService } from './monitor-retail-page.service';
import { MonitorPriorityPageService } from './monitor-priority-page.service';
import { MonitorOverviewPageComponent } from '../components/tabs/monitor-overview-tab/monitor-overview-page.component';
import { MonitorRetailPageComponent } from '../components/tabs/monitor-retail-tab/monitor-retail-page.component';
import { MonitorPriorityPageComponent } from '../components/tabs/monitor-priority-tab/monitor-priority-page.component';
import { BarChartLegend } from '../../shared/models/charts/chart-data-types';
import { ColumnDefinition } from '../../models/column';

const TOTAL_IN_MILLION = 'Total: ($M)';
const EXCEPT_FIELDS = ['coupon', 'subscription'];
const MONITOR_OVERVIEW_LABEL = 'Monitor Overview';
const MONITOR_RETAIL_LABEL = 'Monitor Retail';
const MONITOR_PRIORITY_LABEL = 'Monitor Priority';
const MONITOR_OVERVIEW_TAB = {
  name: MONITOR_OVERVIEW_LABEL,
  priority: 2
};
const MONITOR_RETAIL_TAB = {
  name: MONITOR_RETAIL_LABEL,
  priority: 3
};
const MONITOR_PRIORITY_TAB = {
  name: MONITOR_PRIORITY_LABEL,
  priority: 4
};

@Injectable()
export class ExportMonitorService extends ExportBaseService {
  private monitorServiceSubscription: Subscription;

  // Disable tab rules
  @ReusableStreamProperty
  private readonly isRetailTabDisabled$: Observable<
    boolean
  > = this.retailService.getCategories().pipe(
    map(
      (categories: BarChartLegend[]) =>
        !this.retailService.containsNotRetailsCategories(categories)
    ),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  private readonly isPriorityTabDisabled$: Observable<boolean> = this
    .monitorService.isSoleManagedDeal;

  // Header data streams
  @ReusableStreamProperty
  private readonly overviewHeaders$: Observable<
    ColumnDefinition[]
  > = this.overviewService
    .getColumnDefinitions(
      MonitorOverviewPageComponent.columnDefinition,
      this.overviewService.getCategories()
    )
    .pipe(
      map(columns => columns.filter(column => !!column.label)),
      distinctUntilChanged(equalsCompare)
    );

  @ReusableStreamProperty
  private readonly retailHeaders$: Observable<
    ColumnDefinition[]
  > = this.retailService
    .getColumnDefinitions(
      MonitorRetailPageComponent.columnDefinition,
      this.retailService.getCategories()
    )
    .pipe(
      map(columns => columns.filter(column => !!column.label)),
      distinctUntilChanged(equalsCompare)
    );

  @ReusableStreamProperty
  private readonly priorityHeaders$: Observable<
    ColumnDefinition[]
  > = this.priorityService
    .getColumnDefinitions(
      MonitorPriorityPageComponent.columnDefinition,
      this.priorityService.getCategories()
    )
    .pipe(
      map(columns => columns.filter(column => !!column.label)),
      distinctUntilChanged(equalsCompare)
    );

  // Totals data streams
  @ReusableStreamProperty
  private readonly overviewTotals$: Observable<
    MonitorOverviewViewItem
  > = this.overviewService
    .calculateTotals()
    .pipe(
      map(totals =>
        transformTotals(totals, MonitorOverviewPageComponent.notCategories)
      )
    );

  @ReusableStreamProperty
  private readonly retailTotals$: Observable<
    MonitorOverviewViewItem
  > = this.retailService
    .calculateTotals()
    .pipe(
      map(totals =>
        transformTotals(totals, MonitorRetailPageComponent.notCategories)
      )
    );

  private readonly priorityTotals$: Observable<
    MonitorOverviewViewItem
  > = this.priorityService
    .calculateTotals()
    .pipe(
      map(totals =>
        transformTotals(totals, MonitorPriorityPageComponent.notCategories)
      )
    );

  // Table data streams
  @ReusableStreamProperty
  private readonly overviewData$: Observable<
    MonitorOverviewViewItem[]
  > = this.overviewService.getDataCombinedByMaturities().pipe(
    map(dataToSort => dataToSort.sort(sortOrderMonitorItemByMaturity)),
    map(data =>
      data.map(item =>
        performFinalTransformations(
          item,
          MonitorOverviewPageComponent.notCategories
        )
      )
    )
  );

  @ReusableStreamProperty
  private readonly retailData$: Observable<
    MonitorOverviewViewItem[]
  > = this.retailService.getDataCombinedByMaturities().pipe(
    map(dataToSort => dataToSort.sort(sortOrderMonitorItemByMaturity)),
    map(data =>
      data.map(item =>
        performFinalTransformations(
          item,
          MonitorRetailPageComponent.notCategories
        )
      )
    )
  );

  @ReusableStreamProperty
  private readonly priorityData$: Observable<
    MonitorOverviewViewItem[]
  > = this.priorityService.getDataCombinedByMaturities().pipe(
    map(dataToSort => dataToSort.sort(sortOrderMonitorItemByMaturity)),
    map(data =>
      data.map(item =>
        performFinalTransformations(
          item,
          MonitorPriorityPageComponent.notCategories
        )
      )
    )
  );

  // Push data from store to the source object
  // That was made to push data from outside to the table in order monitor module
  // tslint:disable-next-line
  private readonly monitorServiceData$: Observable<any> = combineLatest(
    this.monitorService.data,
    this.monitorService.retailData,
    this.monitorService.priorityData
  );

  // Monitor stream that combines table data, totals and headers
  // tslint:disable-next-line
  public readonly monitorData$: Observable<TabSettings[]> = combineLatest(
    this.overviewHeaders$,
    this.overviewTotals$,
    this.overviewData$,
    this.retailHeaders$,
    this.retailTotals$,
    this.retailData$,
    this.priorityHeaders$,
    this.priorityTotals$,
    this.priorityData$,
    this.isRetailTabDisabled$,
    this.isPriorityTabDisabled$,
    (...params) => this.getTabs(params)
  );

  constructor(
    private monitorService: MonitorService,
    private overviewService: MonitorOverviewPageService,
    private retailService: MonitorRetailPageService,
    private priorityService: MonitorPriorityPageService
  ) {
    super();
  }

  public subscribe() {
    this.monitorServiceSubscription = this.monitorServiceData$.subscribe(
      ([overviewData, retailData, priorityData]) => {
        this.overviewService.dataSourceSubj = overviewData;
        this.retailService.dataSourceSubj = retailData;
        this.priorityService.dataSourceSubj = priorityData;
      }
    );
  }

  public unsubscribe() {
    this.monitorServiceSubscription.unsubscribe();
  }

  private getTabs(data): TabSettings[] {
    const [
      overviewHeaders,
      overviewTotals,
      overviewData,
      retailHeaders,
      retailTotals,
      retailData,
      priorityHeaders,
      priorityTotals,
      priorityData,
      isRetailTabDisabled,
      isPriorityTabDisabled
    ] = data;

    const tabs: TabSettings[] = [
      this.createTab({
        ...MONITOR_OVERVIEW_TAB,
        header: overviewHeaders,
        totals: toMillion(
          { ...overviewTotals, maturity: TOTAL_IN_MILLION },
          EXCEPT_FIELDS
        ),
        data: toMillion(overviewData, EXCEPT_FIELDS)
      })
    ];

    if (!isRetailTabDisabled) {
      tabs.push(
        this.createTab({
          ...MONITOR_RETAIL_TAB,
          header: retailHeaders,
          totals: toMillion(
            { ...retailTotals, maturity: TOTAL_IN_MILLION },
            EXCEPT_FIELDS
          ),
          data: toMillion(retailData, EXCEPT_FIELDS)
        })
      );
    }

    if (!isPriorityTabDisabled) {
      tabs.push(
        this.createTab({
          ...MONITOR_PRIORITY_TAB,
          header: priorityHeaders,
          totals: toMillion(
            { ...priorityTotals, maturity: TOTAL_IN_MILLION },
            EXCEPT_FIELDS
          ),
          data: toMillion(priorityData, EXCEPT_FIELDS)
        })
      );
    }

    return tabs;
  }
}
